/**
 * Mock data for the Infrastructure dashboard.
 *
 * Everything here is dummy/synthetic data shaped so the Infrastructure page can
 * be wired to a real API later without touching the UI. Swap the exports below
 * for API responses when available.
 */

import type { ChartSlice, ChartSeries } from 'shared/components/Chart/AppChart';

// ─────────────────────────────────────────────
// Infrastructure types (left sidebar)
// ─────────────────────────────────────────────

export interface InfraType {
  key: string;
  label: string;
  icon: string; // PrimeIcons class
}

export const infraTypes: InfraType[] = [
  { key: 'classrooms', label: 'Classrooms', icon: 'fa-solid fa-chalkboard' },
  { key: 'toilets', label: 'Toilets', icon: 'fa-solid fa-toilet' },
  { key: 'urinals', label: 'Urinals', icon: 'fa-solid fa-restroom' },
  {
    key: 'drinking-water',
    label: 'Drinking Water',
    icon: 'fa-solid fa-droplet',
  },
  { key: 'labs', label: 'Labs', icon: 'fa-solid fa-flask' },
  { key: 'furniture', label: 'Furniture', icon: 'fa-solid fa-chair' },
];

// ─────────────────────────────────────────────
// Per-type KPI + chart detail
// ─────────────────────────────────────────────

export interface InfraKpi {
  /** Big percentage value shown on the tile, e.g. "75.8%". */
  value: string;
  /** Uppercase caption under the value. */
  label: string;
  /** PrimeIcons class for the tile badge. */
  icon: string;
  /** Tone: controls the tile color band. */
  tone: 'primary' | 'info' | 'success' | 'warning';
}

export interface InfraStat {
  value: string;
  label: string;
}

export interface InfraDetail {
  /** Four headline KPI tiles. */
  kpis: InfraKpi[];
  /** Six secondary stat tiles (Schools expected, submitted, …). */
  stats: InfraStat[];
  /** Data-entry doughnut (Submitted vs Pending). */
  dataEntry: ChartSlice[];
  /** Data-entry submitted count subtitle, e.g. "43,824 of 57,803 schools". */
  dataEntrySubtitle: string;
  /** vs-Norms doughnut (Met vs Shortfall). */
  vsNorms: ChartSlice[];
  /** Condition doughnut (Good / Needs repair / To be demolished). */
  condition: ChartSlice[];
}

// A compact factory keeps the eight datasets readable and consistent.
function detail(
  overrides: Partial<InfraDetail> & {
    complianceValue: string;
    availabilityValue: string;
    goodValue: string;
    shortfallValue: string;
    expected: string;
    submitted: string;
    pending: string;
    available: string;
    required: string;
    shortfall: string;
    submittedPct: number;
    metPct: number;
    goodPct: number;
    repairPct: number;
    demolishPct: number;
  }
): InfraDetail {
  const o = overrides;
  return {
    kpis: [
      {
        value: o.complianceValue,
        label: 'DATA ENTRY COMPLIANCE',
        icon: 'fa-solid fa-clipboard-check',
        tone: 'primary',
      },
      {
        value: o.availabilityValue,
        label: 'AVAILABILITY VS NORMS',
        icon: 'fa-solid fa-scale-balanced',
        tone: 'info',
      },
      {
        value: o.goodValue,
        label: 'GOOD CONDITION',
        icon: 'fa-solid fa-circle-check',
        tone: 'success',
      },
      {
        value: o.shortfallValue,
        label: 'SHORTFALL GAP',
        icon: 'fa-solid fa-triangle-exclamation',
        tone: 'warning',
      },
    ],
    stats: [
      { value: o.expected, label: 'SCHOOLS EXPECTED' },
      { value: o.submitted, label: 'SCHOOLS SUBMITTED' },
      { value: o.pending, label: 'ENTRY PENDING' },
      { value: o.available, label: 'AVAILABLE UNITS' },
      { value: o.required, label: 'REQUIRED BY NORMS' },
      { value: o.shortfall, label: 'SHORTFALL' },
    ],
    dataEntry: [
      { label: 'Submitted', value: o.submittedPct, color: '#4a9e5c' },
      {
        label: 'Pending',
        value: Number((100 - o.submittedPct).toFixed(2)),
        color: '#d9584f',
      },
    ],
    dataEntrySubtitle: overrides.dataEntrySubtitle ?? '',
    vsNorms: [
      { label: 'Met', value: o.metPct, color: '#3b82f6' },
      {
        label: 'Shortfall',
        value: Number((100 - o.metPct).toFixed(2)),
        color: '#d9584f',
      },
    ],
    condition: [
      { label: 'Good condition', value: o.goodPct, color: '#2e8b48' },
      { label: 'Needs repair', value: o.repairPct, color: '#f0a835' },
      {
        label: 'To be demolished',
        value: o.demolishPct ?? 0,
        color: '#d9584f',
      },
    ],
  };
}

export const infraDetails: Record<string, InfraDetail> = {
  classrooms: detail({
    complianceValue: '75.8%',
    availabilityValue: '95.2%',
    goodValue: '83.1%',
    shortfallValue: '7.8%',
    expected: '57,803',
    submitted: '43,824',
    pending: '13,979',
    available: '2,57,961',
    required: '2,70,993',
    shortfall: '21,177',
    dataEntrySubtitle: '43,824 of 57,803 schools',
    submittedPct: 75.82,
    metPct: 95.19,
    goodPct: 83.12,
    repairPct: 14.94,
    demolishPct: 1.94,
  }),
  toilets: detail({
    complianceValue: '78.4%',
    availabilityValue: '104%',
    goodValue: '88.6%',
    shortfallValue: '4.9%',
    expected: '57,803',
    submitted: '45,318',
    pending: '12,485',
    available: '2,69,660',
    required: '2,59,356',
    shortfall: '12,569',
    dataEntrySubtitle: '45,318 of 57,803 schools',
    submittedPct: 78.4,
    metPct: 96.1,
    goodPct: 88.63,
    repairPct: 9.42,
    demolishPct: 1.95,
  }),
  urinals: detail({
    complianceValue: '74.1%',
    availabilityValue: '92.9%',
    goodValue: '84.2%',
    shortfallValue: '8.3%',
    expected: '57,803',
    submitted: '42,832',
    pending: '14,971',
    available: '1,76,829',
    required: '1,90,346',
    shortfall: '15,870',
    dataEntrySubtitle: '42,832 of 57,803 schools',
    submittedPct: 74.1,
    metPct: 92.9,
    goodPct: 84.2,
    repairPct: 13.5,
    demolishPct: 2.3,
  }),
  'drinking-water': detail({
    complianceValue: '80.2%',
    availabilityValue: '96.6%',
    goodValue: '66.9%',
    shortfallValue: '3.5%',
    expected: '57,803',
    submitted: '46,358',
    pending: '11,445',
    available: '79,323',
    required: '82,131',
    shortfall: '5,723',
    dataEntrySubtitle: '46,358 of 57,803 schools',
    submittedPct: 80.2,
    metPct: 96.6,
    goodPct: 66.9,
    repairPct: 27.6,
    demolishPct: 5.5,
  }),
  labs: detail({
    complianceValue: '68.7%',
    availabilityValue: '50.2%',
    goodValue: '100%',
    shortfallValue: '49.8%',
    expected: '57,803',
    submitted: '39,712',
    pending: '18,091',
    available: '29,624',
    required: '59,003',
    shortfall: '29,379',
    dataEntrySubtitle: '39,712 of 57,803 schools',
    submittedPct: 68.7,
    metPct: 50.2,
    goodPct: 100,
    repairPct: 0,
    demolishPct: 0,
  }),
  furniture: detail({
    complianceValue: '73.7%',
    availabilityValue: '75.9%',
    goodValue: '80.5%',
    shortfallValue: '24.1%',
    expected: '57,803',
    submitted: '42,603',
    pending: '15,200',
    available: '34,97,705',
    required: '46,08,149',
    shortfall: '11,10,444',
    dataEntrySubtitle: '42,603 of 57,803 schools',
    submittedPct: 73.7,
    metPct: 75.9,
    goodPct: 80.5,
    repairPct: 16.4,
    demolishPct: 3.1,
  }),
};

// ─────────────────────────────────────────────
// Data-entry compliance by period (combo chart + table)
// ─────────────────────────────────────────────

export interface CompliancePeriodRow {
  period: string;
  expected: number;
  submitted: number;
  pending: number;
  compliance: number; // %
}

export const infraComplianceByPeriod: CompliancePeriodRow[] = [
  {
    period: 'April 2025-26',
    expected: 57803,
    submitted: 31933,
    pending: 25870,
    compliance: 55.2,
  },
  {
    period: 'May 2025-26',
    expected: 57803,
    submitted: 34982,
    pending: 22821,
    compliance: 60.5,
  },
  {
    period: 'June 2025-26',
    expected: 57803,
    submitted: 41928,
    pending: 15875,
    compliance: 72.5,
  },
  {
    period: 'July 2025-26',
    expected: 57803,
    submitted: 44778,
    pending: 13025,
    compliance: 77.5,
  },
  {
    period: 'August 2025-26',
    expected: 57803,
    submitted: 46108,
    pending: 11695,
    compliance: 79.8,
  },
  {
    period: 'September 2025-26',
    expected: 57803,
    submitted: 46331,
    pending: 11472,
    compliance: 80.2,
  },
  {
    period: 'April 2026-27',
    expected: 57803,
    submitted: 27926,
    pending: 29877,
    compliance: 48.3,
  },
  {
    period: 'May 2026-27',
    expected: 57803,
    submitted: 30957,
    pending: 26846,
    compliance: 53.6,
  },
  {
    period: 'June 2026-27',
    expected: 57803,
    submitted: 38078,
    pending: 19725,
    compliance: 65.9,
  },
  {
    period: 'July 2026-27',
    expected: 57803,
    submitted: 40851,
    pending: 16952,
    compliance: 70.7,
  },
  {
    period: 'August 2026-27',
    expected: 57803,
    submitted: 42603,
    pending: 15200,
    compliance: 73.7,
  },
  {
    period: 'September 2026-27',
    expected: 57803,
    submitted: 43824,
    pending: 13979,
    compliance: 75.8,
  },
];

export function buildComplianceSeries(): ChartSeries[] {
  return [
    {
      label: 'Submitted',
      data: infraComplianceByPeriod.map((c) => c.submitted),
      color: '#4a9e5c',
    },
    {
      label: 'Pending',
      data: infraComplianceByPeriod.map((c) => c.pending),
      color: '#d9584f',
    },
    {
      label: 'Compliance %',
      data: infraComplianceByPeriod.map((c) => c.compliance),
      color: '#2f5fb0',
      asLine: true,
      useRightAxis: true,
      showPointLabels: true,
      pointLabelSuffix: '%',
    },
  ];
}

// ─────────────────────────────────────────────
// All infrastructure types — Available vs Shortfall (grouped bar + overview)
// ─────────────────────────────────────────────

export interface InfraOverviewRow {
  key: string;
  type: string;
  icon: string;
  available: number;
  norms: number;
  shortfall: number;
  surplus: number;
  availability: string; // e.g. "95.2%"
  coverage: string; // e.g. "99.7%"
  good: string; // e.g. "83.1%"
}

export const infraOverview: InfraOverviewRow[] = [
  {
    key: 'classrooms',
    type: 'Classrooms',
    icon: 'fa-solid fa-chalkboard',
    available: 257961,
    norms: 270993,
    shortfall: 21177,
    surplus: 8145,
    availability: '95.2%',
    coverage: '99.7%',
    good: '83.1%',
  },
  {
    key: 'toilets',
    type: 'Toilets',
    icon: 'fa-solid fa-toilet',
    available: 269660,
    norms: 259356,
    shortfall: 12569,
    surplus: 22873,
    availability: '104%',
    coverage: '99.6%',
    good: '93.1%',
  },
  {
    key: 'urinals',
    type: 'Urinals',
    icon: 'fa-solid fa-restroom',
    available: 176829,
    norms: 190346,
    shortfall: 15870,
    surplus: 2353,
    availability: '92.9%',
    coverage: '98.9%',
    good: '94.2%',
  },
  {
    key: 'drinking-water',
    type: 'Drinking Water',
    icon: 'fa-solid fa-droplet',
    available: 79323,
    norms: 82131,
    shortfall: 5723,
    surplus: 2915,
    availability: '96.6%',
    coverage: '99.5%',
    good: '66.9%',
  },
  {
    key: 'labs',
    type: 'Labs',
    icon: 'fa-solid fa-flask',
    available: 29624,
    norms: 59003,
    shortfall: 29379,
    surplus: 0,
    availability: '50.2%',
    coverage: '57.7%',
    good: '100%',
  },
  {
    key: 'furniture',
    type: 'Furniture',
    icon: 'fa-solid fa-chair',
    available: 3497705,
    norms: 4608149,
    shortfall: 1110444,
    surplus: 0,
    availability: '75.9%',
    coverage: '99.7%',
    good: '80.5%',
  },
];

export const infraOverviewLabels = infraOverview.map((r) => r.type);

export function buildOverviewSeries(): ChartSeries[] {
  return [
    {
      label: 'Available',
      data: infraOverview.map((r) => r.available),
      color: '#8bc34a',
    },
    {
      label: 'Shortfall',
      data: infraOverview.map((r) => r.shortfall),
      color: '#c0392b',
    },
  ];
}

// ─────────────────────────────────────────────
// Per-type district drill-down table
// ─────────────────────────────────────────────

export interface InfraDistrictRow {
  name: string;
  available: number;
  norms: number;
  shortfall: number;
  availability: string;
  coverage: string;
  good: string;
  gap: string;
}

const districtNames = [
  'Tiruvallur',
  'Chennai',
  'Kancheepuram',
  'Chengalpattu',
  'Ranipet',
  'Vellore',
  'Tirupathur',
  'Dharmapuri',
  'Tiruvannamalai',
  'Villupuram',
  'Kallakurichi',
  'Salem',
  'Namakkal',
  'Erode',
  'Coimbatore',
  'Tiruppur',
  'Karur',
  'Trichy',
  'Perambalur',
  'Ariyalur',
  'Cuddalore',
  'Nagapattinam',
  'Thanjavur',
  'Tiruvarur',
  'Pudukottai',
  'Madurai',
  'Theni',
  'Dindigul',
  'Ramanathapuram',
  'Virudhunagar',
  'Sivagangai',
  'Tirunelveli',
  'Thoothukudi',
  'Kanniyakumari',
];

/** Deterministic pseudo-random helper so the mock stays stable per render. */
function seeded(i: number, salt: number) {
  return Math.abs(Math.sin((i + 1) * (salt + 1.3)));
}

/**
 * Build a district-level table for a given infrastructure type. Values are
 * derived from the type's state-level totals so they look coherent.
 */
export function getInfraDistricts(typeKey: string): InfraDistrictRow[] {
  const overview = infraOverview.find((o) => o.key === typeKey);
  const baseAvail = overview ? overview.available : 250000;
  const perDistrict = baseAvail / districtNames.length;

  return districtNames.map((name, i) => {
    const avail = Math.round(perDistrict * (0.55 + seeded(i, 1) * 0.9));
    const norms = Math.round(avail * (1 + seeded(i, 2) * 0.14));
    const shortfall = Math.max(norms - avail, 0);
    const availabilityPct = norms ? (avail / norms) * 100 : 100;
    const goodPct = 78 + seeded(i, 3) * 18;
    const coveragePct = 96 + seeded(i, 4) * 3.8; // 96–99.8%
    const gapPct = Math.max(100 - availabilityPct, 0);
    return {
      name,
      available: avail,
      norms,
      shortfall,
      availability: `${availabilityPct.toFixed(1)}%`,
      coverage: `${coveragePct.toFixed(1)}%`,
      good: `${goodPct.toFixed(1)}%`,
      gap: `${gapPct.toFixed(1)}%`,
    };
  });
}

// ─────────────────────────────────────────────
// Alarming indicators (Infrastructure → Alarming sub-tab)
// ─────────────────────────────────────────────

export interface InfraAlertRow {
  severity: 'High' | 'Medium' | 'Low';
  district: string;
  alert: string; // e.g. "Labs & Smart Boards: Below Norms"
  measured: string; // e.g. "46.2% of norms; shortfall 540"
}

export interface AlertCategory {
  key: string;
  label: string; // "Labs & Smart Boards: Below Norms"
  count: number;
  color: string;
}

// The alarm categories shown as bars / filter chips (mirrors the reference UI).
export const infraAlertCategories: AlertCategory[] = [
  {
    key: 'labs-below-norms',
    label: 'Labs & Smart Boards: Below Norms',
    count: 38,
    color: '#f4c430',
  },
  {
    key: 'labs-low-coverage',
    label: 'Labs & Smart Boards: Low Coverage',
    count: 38,
    color: '#f0803c',
  },
  {
    key: 'safety-below-norms',
    label: 'Safety & Security: Below Norms',
    count: 38,
    color: '#8bc34a',
  },
  {
    key: 'amenities-below-norms',
    label: 'Building & Amenities: Below Norms',
    count: 38,
    color: '#8e2f4c',
  },
  {
    key: 'furniture-below-norms',
    label: 'Furniture: Below Norms',
    count: 38,
    color: '#2aa198',
  },
  {
    key: 'water-poor-condition',
    label: 'Drinking Water: Poor Condition',
    count: 37,
    color: '#2f7ec4',
  },
];

export const infraAlarmingSummary = {
  high: 109,
  medium: 118,
  total: 227,
  districtsFlagged: 38,
};

// Threshold note shown under the alert-details table title.
export const infraAlertThresholds =
  'availability < 85% of norms · good condition < 70% · coverage < 75%';

/**
 * Build the full alert-details list (deterministic mock). Distributes the
 * category counts into rows with a plausible severity + measured string.
 */
export function getInfraAlerts(): InfraAlertRow[] {
  const rows: InfraAlertRow[] = [];
  const names = districtNames;

  infraAlertCategories.forEach((cat, ci) => {
    for (let i = 0; i < cat.count; i++) {
      const district = names[(ci * 7 + i) % names.length];
      const pctOfNorms = 45 + seeded(ci * 40 + i, 5) * 40; // 45–85%
      const shortfall = Math.round(400 + seeded(ci * 40 + i, 6) * 900);
      // High when far below norms; Medium otherwise.
      const severity: InfraAlertRow['severity'] =
        pctOfNorms < 60 ? 'High' : 'Medium';
      const measured = cat.label.includes('Coverage')
        ? `coverage ${pctOfNorms.toFixed(1)}%`
        : cat.label.includes('Condition')
          ? `good condition ${pctOfNorms.toFixed(1)}%`
          : `${pctOfNorms.toFixed(1)}% of norms; shortfall ${shortfall}`;
      rows.push({ severity, district, alert: cat.label, measured });
    }
  });

  // Sort worst first (High before Medium), then by district for stability.
  const rank = { High: 0, Medium: 1, Low: 2 } as const;
  return rows.sort(
    (a, b) =>
      rank[a.severity] - rank[b.severity] ||
      a.district.localeCompare(b.district)
  );
}

/**
 * Per-district "Data Entry Compliance" ranked worst-first (for the left bar
 * chart + the compliance table). Values derived deterministically.
 */
export interface InfraComplianceDistrictRow {
  district: string;
  expected: number;
  submitted: number;
  pending: number;
  compliance: string; // "73.9%"
}

export function getInfraComplianceByDistrict(): InfraComplianceDistrictRow[] {
  return districtNames
    .map((district, i) => {
      const expected = Math.round(600 + seeded(i, 7) * 2200);
      const compliancePct = 73 + seeded(i, 8) * 12; // 73–85%
      const submitted = Math.round((expected * compliancePct) / 100);
      const pending = expected - submitted;
      return {
        district,
        expected,
        submitted,
        pending,
        compliance: `${compliancePct.toFixed(1)}%`,
        _sort: compliancePct,
      };
    })
    .sort((a, b) => a._sort - b._sort) // worst (lowest %) first
    .map(({ _sort, ...row }) => row);
}

/**
 * Per-district shortfall % for the selected infrastructure type (right bar
 * chart), ranked highest-gap first.
 */
export function getInfraShortfallByDistrict(
  typeKey: string
): { district: string; gap: number }[] {
  return getInfraDistricts(typeKey)
    .map((r) => ({ district: r.name, gap: Number(r.gap.replace('%', '')) }))
    .sort((a, b) => b.gap - a.gap);
}

// ─────────────────────────────────────────────
// Caution — hard checks (Infrastructure → Caution sub-tab)
// ─────────────────────────────────────────────

export interface InfraCautionCheck {
  key: string;
  title: string;
  icon: string; // PrimeIcons class
  count: number;
  severity: 'High' | 'Medium';
  meaning: string;
}

export const infraCautionChecks: InfraCautionCheck[] = [
  {
    key: 'water-not-functional',
    title: 'Water Source Not Functional',
    icon: 'pi pi-filter-slash',
    count: 4646,
    severity: 'High',
    meaning: 'A source exists but none of it is working',
  },
  {
    key: 'no-drinking-water',
    title: 'No Drinking Water',
    icon: 'pi pi-cloud',
    count: 229,
    severity: 'High',
    meaning: 'No drinking water source of any kind reported',
  },
  {
    key: 'zero-toilets',
    title: 'Zero Toilets',
    icon: 'pi pi-home',
    count: 162,
    severity: 'High',
    meaning: 'No toilet of any kind reported at the school',
  },
  {
    key: 'zero-classrooms',
    title: 'Zero Classrooms',
    icon: 'pi pi-th-large',
    count: 124,
    severity: 'High',
    meaning: 'No classroom of its own — likely temporary or shared premises',
  },
  {
    key: 'data-not-submitted',
    title: 'Data Not Submitted',
    icon: 'pi pi-file-excel',
    count: 13979,
    severity: 'Medium',
    meaning:
      'No infrastructure return filed for September 2026-27 — figures above exclude these schools',
  },
  {
    key: 'zero-urinals',
    title: 'Zero Urinals',
    icon: 'pi pi-inbox',
    count: 468,
    severity: 'Medium',
    meaning: 'No urinal reported at the school',
  },
  {
    key: 'no-safety',
    title: 'No Safety Provision',
    icon: 'pi pi-shield',
    count: 378,
    severity: 'Medium',
    meaning: 'No CCTV, fire extinguisher, compound wall or incinerator',
  },
  {
    key: 'zero-furniture',
    title: 'Zero Furniture',
    icon: 'pi pi-box',
    count: 110,
    severity: 'Medium',
    meaning: 'No benches or desks reported',
  },
];

export const infraCautionTotalFlagged = 19812;

export interface InfraCautionSchoolRow {
  district: string;
  block: string;
  school: string;
  udise: string;
  management: string;
  students: number;
  finding: string;
}

const CAUTION_BLOCKS = [
  'Hosur',
  'Poonamallee',
  'Anna Nagar',
  'Thiruparamkundram',
  'Perundurai',
  'St.Thomas Mount',
  'Thiru Vi Ka Nagar',
  'Morappur',
  'Trichy-Urban',
  'Kundrathur',
  'Uthangarai',
  'Kaniyambadi',
  'Salem-Urban',
  'Madhanur',
];
const CAUTION_MGMT = [
  'Unaided (Private) School',
  'Fully Aided School',
  'Government School',
  'Corporation School',
  'Municipal School',
];
const CAUTION_SCHOOLS = [
  'MAHARISHI VIDYA MANDIR SENIOR SECONDARY SCHOOL HOSUR',
  'VELAMMAL VIDYALAYA',
  'MAHARISHI VIDYA MANDIR SENIOR SECONDARY SCHOOL',
  'THE TVS SCHOOL, TVS NAGAR',
  'BHARATHI MATRIC HIGHER SECONDARY SCHOOL, SHANMUGAPURAM',
  'SRI CHAITHANYA TECHNO SCHOOL',
  'EVERWIN MATRIC HSS',
  'I.V.L. MHSS, IRUMATHUR',
  'NAZARETH MATRIC HR SEC SCHOOL, KAMARAJ NAGAR, AVADI',
  'HOLY CROSS GIRLS HSS, TEPPAKULAM, TRICHY-2',
  'Shree Niketan Patasala CBSE School, Varatharajapuram',
  'R.P.S MATRIC HR SEC SCHOOL',
  'SEVENTH DAY ADVENTIST MATRIC HSS, OTTERI',
  'JAYARANI MATRIC HR. SEC. SCHOOL, NETHIMEDU',
  'KSR MATRIC PUBLIC HIGHER SECONDARY SCHOOL, MADHANUR',
];
const CAUTION_DISTRICTS = [
  'Krishnagiri',
  'Tiruvallur',
  'Chennai',
  'Madurai',
  'Erode',
  'Chengalpattu',
  'Dharmapuri',
  'Tiruchirappalli',
  'Kancheepuram',
  'Vellore',
  'Salem',
  'Tirupathur',
];

function cautionSeeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Finding phrasing per check type.
function cautionFinding(checkKey: string, rnd: () => number): string {
  switch (checkKey) {
    case 'water-not-functional':
      return `${10 + Math.floor(rnd() * 40)} units, none functional`;
    case 'no-drinking-water':
      return 'No source reported';
    case 'zero-toilets':
      return '0 toilets reported';
    case 'zero-classrooms':
      return '0 own classrooms';
    case 'data-not-submitted':
      return 'No return filed';
    case 'zero-urinals':
      return '0 urinals reported';
    case 'no-safety':
      return 'No safety provision';
    case 'zero-furniture':
      return '0 benches / desks';
    default:
      return 'Flagged';
  }
}

/** Rows of flagged schools for a given caution check (deterministic mock). */
export function getInfraCautionSchools(
  checkKey: string
): InfraCautionSchoolRow[] {
  const check = infraCautionChecks.find((c) => c.key === checkKey);
  const rnd = cautionSeeded(`infra-caution:${checkKey}`);
  const count = Math.min(check?.count ?? 20, 45);
  return Array.from({ length: count }, (_, i) => {
    const udise = `331${11000000 + Math.floor(rnd() * 900000)}`;
    const students = 2800 + Math.floor(rnd() * 4200);
    return {
      district: CAUTION_DISTRICTS[i % CAUTION_DISTRICTS.length],
      block: CAUTION_BLOCKS[i % CAUTION_BLOCKS.length],
      school: CAUTION_SCHOOLS[i % CAUTION_SCHOOLS.length],
      udise,
      management: CAUTION_MGMT[Math.floor(rnd() * CAUTION_MGMT.length)],
      students,
      finding: cautionFinding(checkKey, rnd),
    };
  });
}
